import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  Button,
  Box,
  TextField,
  IconButton,
  InputBase,
  Alert,
  CircularProgress,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Save, Preview, Help, Add, EditOutlined, SlideshowOutlined } from '@mui/icons-material';
import { EmptyState, IconTile } from './pagekit';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import DraggableSlide from './powerPointRichTemplate/DraggableSlide';
import PowerPointUploadSection from './powerPointRichTemplate/PowerPointUploadSection';
import PowerPointJsonPanel from './powerPointRichTemplate/PowerPointJsonPanel';
import PowerPointHelpDialog from './powerPointRichTemplate/PowerPointHelpDialog';
import { buildVariableCatalog, mergeVariableCatalogDescriptions } from '../utils/presentationAiArtifacts';
import { registerUnsavedWorkFlusher, templateEditorPath } from '../utils/navigation';

/**
 * Create or edit a JSON-based PowerPoint template with rich HTML per slide.
 * @returns {React.ReactElement}
 */
function PowerPointRichTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { state, actions } = useApp();

  const [slides, setSlides] = useState([
    {
      id: uuidv4(),
      type: 'title_slide',
      content: '<h1>{{presentation_title}}</h1><p>Presented by <strong>{{author}}</strong></p>',
    },
    {
      id: uuidv4(),
      type: 'content_slide',
      content:
        '<h2>Introduction</h2><ul><li>{{introduction_point_1}}</li><li>{{introduction_point_2}}</li><li>{{introduction_point_3}}</li></ul>',
    },
  ]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [templateMeta, setTemplateMeta] = useState({
    theme: null,
    sourceFileName: null,
    /** @type {{ name: string, description: string, slideNumber?: number }[] | null} */
    variableCatalogHints: null,
    extractionWriterBrief: null,
    extractionAiApplied: null,
    extractionAiError: null,
    extractionAiSkippedReason: null,
    /** @type {'draft' | 'ready' | 'processing' | 'failed' | null} */
    status: null,
  });

  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatedJson, setGeneratedJson] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [collapsedById, setCollapsedById] = useState({});

  const isEditing = Boolean(templateId && templateId !== 'new');
  /** Template id (or 'new') whose data is loaded; later template list updates do not reload it. */
  const loadedIdRef = useRef(null);
  /** Snapshot of the form as last loaded or saved (null until loaded); see formSnapshot. */
  const baselineRef = useRef(null);
  const needsBaselineRef = useRef(true);

  // Load once per template id, after AppContext has loaded the templates (a direct link to the
  // editor used to bounce to the list before the templates arrived).
  useEffect(() => {
    const key = isEditing ? templateId : 'new';
    if (loadedIdRef.current === key) return;
    if (isEditing && !state.initialLoadComplete) {
      setLoading(true);
      return;
    }
    const loadTemplate = () => {
      setLoading(true);
      try {
        if (isEditing) {
          const template = state.templates.find((t) => t.id === templateId);
          if (template) {
            setFormData({
              name: template.name || '',
              description: template.description || '',
            });

            try {
              const parsedContent = JSON.parse(template.content);
              if (parsedContent.slides && Array.isArray(parsedContent.slides)) {
                const slidesWithIds = parsedContent.slides.map((slide) => ({
                  ...slide,
                  id: slide.id && typeof slide.id === 'string' ? slide.id : uuidv4(),
                  type: slide.type || 'content_slide',
                  content: typeof slide.content === 'string' ? slide.content : '<p></p>',
                  ...(slide.aiNotes && typeof slide.aiNotes === 'object' ? { aiNotes: slide.aiNotes } : {}),
                }));
                setSlides(slidesWithIds);
                setTemplateMeta({
                  theme: parsedContent.theme || null,
                  sourceFileName: parsedContent.sourceFileName || null,
                  variableCatalogHints: Array.isArray(parsedContent.variableCatalog)
                    ? parsedContent.variableCatalog
                    : null,
                  extractionWriterBrief: parsedContent.extractionWriterBrief || null,
                  extractionAiApplied:
                    typeof parsedContent.extractionAiApplied === 'boolean'
                      ? parsedContent.extractionAiApplied
                      : null,
                  extractionAiError: parsedContent.extractionAiError || null,
                  extractionAiSkippedReason: parsedContent.extractionAiSkippedReason || null,
                  status: template.status || null,
                });
              }
            } catch (error) {
              console.error('Error parsing template content:', error);
            }
          } else {
            navigate('/templates');
            return;
          }
        }
        loadedIdRef.current = key;
        needsBaselineRef.current = true;
      } catch (error) {
        console.error('Error loading template:', error);
        setErrors({ general: 'Failed to load template data' });
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [templateId, isEditing, state.templates, state.initialLoadComplete, navigate]);

  const templateObject = useMemo(() => {
    const name = formData.name || 'My Rich Presentation Template';
    const heuristic = buildVariableCatalog(slides);
    const catalog =
      templateMeta.variableCatalogHints && templateMeta.variableCatalogHints.length > 0
        ? mergeVariableCatalogDescriptions(templateMeta.variableCatalogHints, heuristic)
        : heuristic;
    const template = {
      template_name: name,
      template_type: 'powerpoint',
      slides: slides.map(({ id, type, content, aiNotes }) => {
        const row = { id, type, content };
        if (aiNotes && typeof aiNotes === 'object') {
          row.aiNotes = aiNotes;
        }
        return row;
      }),
      variableCatalog: catalog,
    };
    if (templateMeta.theme) {
      template.theme = templateMeta.theme;
    }
    if (templateMeta.sourceFileName) {
      template.sourceFileName = templateMeta.sourceFileName;
    }
    if (templateMeta.extractionWriterBrief) {
      template.extractionWriterBrief = templateMeta.extractionWriterBrief;
    }
    if (typeof templateMeta.extractionAiApplied === 'boolean') {
      template.extractionAiApplied = templateMeta.extractionAiApplied;
    }
    if (templateMeta.extractionAiError) {
      template.extractionAiError = templateMeta.extractionAiError;
    }
    if (templateMeta.extractionAiSkippedReason) {
      template.extractionAiSkippedReason = templateMeta.extractionAiSkippedReason;
    }
    return template;
  }, [slides, formData.name, templateMeta]);

  useEffect(() => {
    setGeneratedJson(JSON.stringify(templateObject, null, 2));
  }, [templateObject]);

  /** Everything a save writes, as one comparable string (dirty = differs from the baseline). */
  const formSnapshot = useMemo(
    () => JSON.stringify([formData.name, formData.description, templateObject]),
    [formData.name, formData.description, templateObject]
  );
  const formSnapshotRef = useRef(formSnapshot);
  formSnapshotRef.current = formSnapshot;

  // Baseline right after a load, so loading itself never counts as an edit.
  useEffect(() => {
    if (loading || !needsBaselineRef.current) return;
    needsBaselineRef.current = false;
    baselineRef.current = formSnapshot;
  }, [loading, formSnapshot]);

  /**
   * Whether there are edits since the last load or save.
   * @returns {boolean} True with unsaved edits.
   */
  const isDirty = useCallback(
    () => baselineRef.current !== null && formSnapshotRef.current !== baselineRef.current,
    []
  );

  const addSlide = () => {
    const newSlide = {
      id: uuidv4(),
      type: 'content_slide',
      content: '<p>Enter slide content here...</p>',
    };
    setSlides([...slides, newSlide]);
  };

  const removeSlide = (id) => {
    setSlides(slides.filter((slide) => slide.id !== id));
  };

  const updateSlide = (id, field, value) => {
    setSlides(slides.map((slide) => (slide.id === id ? { ...slide, [field]: value } : slide)));
  };

  const moveSlide = (fromIndex, toIndex) => {
    const updatedSlides = [...slides];
    const [movedSlide] = updatedSlides.splice(fromIndex, 1);
    updatedSlides.splice(toIndex, 0, movedSlide);
    setSlides(updatedSlides);
  };

  const toggleSlideCollapsed = (id) => {
    setCollapsedById((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const collapseAll = () => {
    setCollapsedById(Object.fromEntries(slides.map((s) => [s.id, true])));
  };

  const expandAll = () => {
    setCollapsedById(Object.fromEntries(slides.map((s) => [s.id, false])));
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(generatedJson)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = generatedJson;
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy JSON: ', err);
        }
        document.body.removeChild(textarea);
      });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (slides.length === 0) {
      newErrors.slides = 'At least one slide is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Validate and write the template (handleSave serializes calls to this).
   * @param {{stay?: boolean}} [opts] - Stay on the page (logout flush) instead of returning to the list.
   * @returns {Promise<boolean>} True when everything edited so far is saved.
   */
  const saveTemplate = async ({ stay = false } = {}) => {
    if (!validateForm()) return false;

    setSaving(true);
    setErrors({});
    const savedSnapshot = formSnapshot;

    try {
      const promotesToReady = templateMeta.status === 'draft' || !isEditing;
      const templateData = {
        ...formData,
        // Built from the current state (generatedJson trails it by one effect).
        content: JSON.stringify(templateObject, null, 2),
        template_type: 'powerpoint',
        // Saving a draft for the first time promotes it to ready-for-generation.
        ...(promotesToReady ? { status: 'ready' } : {}),
      };

      // AppContext reports failures as { success: false } instead of throwing.
      const result = isEditing
        ? await actions.updateTemplate(templateId, templateData)
        : await actions.createTemplate(templateData);
      if (!result || result.success === false) throw new Error(result?.error || 'Save failed');

      baselineRef.current = savedSnapshot;
      if (!stay) {
        navigate('/templates');
        return true;
      }
      if (promotesToReady) setTemplateMeta((prev) => ({ ...prev, status: 'ready' }));
      const newId = !isEditing ? result?.data?.id : null;
      if (newId) {
        // Keep editing the created template at its own URL without reloading the form.
        loadedIdRef.current = newId;
        navigate(templateEditorPath({ templateId: newId, kind: 'powerpoint-rich' }), { replace: true });
      }
      return formSnapshotRef.current === savedSnapshot;
    } catch (error) {
      console.error('Error saving template:', error);
      setErrors({ general: 'Failed to save template. Please try again.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveTemplateRef = useRef(saveTemplate);
  saveTemplateRef.current = saveTemplate;
  /** The running save, so overlapping triggers (button, logout flush) never write twice. */
  const savePromiseRef = useRef(null);

  /**
   * Save the template, one save at a time: a trigger that arrives while a save runs waits for
   * it and only saves again if there are still unsaved edits.
   * @param {{stay?: boolean}} [opts] - Stay on the page instead of returning to the list.
   * @returns {Promise<boolean>} True when everything edited so far is saved.
   */
  const handleSave = useCallback(async (opts) => {
    while (savePromiseRef.current) {
      await savePromiseRef.current.catch(() => false);
      if (!isDirty()) return true;
    }
    const attempt = saveTemplateRef.current(opts);
    savePromiseRef.current = attempt;
    try {
      return await attempt;
    } finally {
      if (savePromiseRef.current === attempt) savePromiseRef.current = null;
    }
  }, [isDirty]);

  // Actions that unmount the page without a router navigation (logging out) save pending
  // edits through this first.
  useEffect(
    () => registerUnsavedWorkFlusher(async () => (isDirty() ? handleSave({ stay: true }) : true)),
    [handleSave, isDirty]
  );

  const handleCancel = () => {
    navigate('/templates');
  };

  const countVariables = (slideList) => {
    const allText = slideList.map((slide) => `${slide.content || ''}`).join(' ');
    const matches = allText.match(/\{\{[^}]+\}\}/g);
    return matches ? matches.length : 0;
  };

  if (loading) {
    return (
      <Box role="progressbar" aria-label="Loading template" aria-busy="true" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Skeleton variant="rounded" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={240} height={28} />
            <Skeleton variant="text" width={140} height={18} />
          </Box>
          <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 999 }} />
        </Box>
        {[0, 1].map((key) => (
          <Skeleton key={key} variant="rounded" height={220} sx={{ mb: 2, maxWidth: 900 }} />
        ))}
      </Box>
    );
  }

  const variableCount = countVariables(slides);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 'var(--q-page-sticky-top, 0px)',
            zIndex: 20,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 1, sm: 2 },
            py: 1,
            bgcolor: 'var(--q-surface)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Back to templates">
            <IconButton onClick={handleCancel} aria-label="Back to templates">
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <IconTile size={36}><SlideshowOutlined /></IconTile>
          </Box>
          <Box sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <InputBase
              value={formData.name}
              placeholder={isEditing ? 'Template name' : 'Name your PowerPoint template'}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              inputProps={{ 'aria-label': 'Template name', maxLength: 200, 'aria-invalid': Boolean(errors.name) }}
              sx={{
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                maxWidth: 480,
                '& input': { px: 0.75, py: '1px', ml: -0.75, borderRadius: '6px', border: '1px solid transparent' },
                '& input:hover': { borderColor: 'divider' },
                '& input:focus': { borderColor: 'var(--q-accent)' },
              }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, fontSize: 12.5, color: 'text.secondary' }}>
              <span>Templates</span>
              <span aria-hidden="true">·</span>
              <span>PowerPoint</span>
              <span aria-hidden="true">·</span>
              <span>{slides.length} slide{slides.length === 1 ? '' : 's'}, {variableCount} variable{variableCount === 1 ? '' : 's'}</span>
              {templateMeta.status === 'draft' && <Chip label="Draft" size="small" color="warning" />}
            </Box>
          </Box>
          <Tooltip title="How PowerPoint templates work">
            <IconButton aria-label="PowerPoint template help" onClick={() => setHelpDialogOpen(true)}>
              <Help />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={() => setPreviewMode(!previewMode)}
            startIcon={previewMode ? <EditOutlined /> : <Preview />}
            aria-pressed={previewMode}
            sx={{ borderRadius: 999 }}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button
            onClick={() => handleSave()}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ borderRadius: 999 }}
          >
            {saving ? 'Saving…' : isEditing ? 'Save template' : 'Create template'}
          </Button>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(errors.name || errors.general) && (
            <Alert severity="error">{errors.name || errors.general}</Alert>
          )}
          {templateMeta.status === 'draft' && (
            <Alert severity="info">
              This template is a <strong>draft</strong> imported from PowerPoint. Review the slides and variable
              descriptions, then save to make it available for document generation.
            </Alert>
          )}
          {templateMeta.extractionAiError && (
            <Alert severity="warning">
              AI restructuring reported an issue: {templateMeta.extractionAiError}. Heuristic slides were kept where
              the AI pass did not succeed.
            </Alert>
          )}
          {templateMeta.extractionAiSkippedReason && !templateMeta.extractionAiError && (
            <Alert severity="info">AI refinement was skipped: {templateMeta.extractionAiSkippedReason}</Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', lg: 'nowrap' }, alignItems: 'flex-start', gap: 3 }}>
            <Box sx={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Description (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  maxRows={3}
                />
                {!isEditing && <PowerPointUploadSection />}
                {templateMeta.sourceFileName && (
                  <Chip label={`Source: ${templateMeta.sourceFileName}`} size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="h2" sx={{ flex: 1, fontSize: 15 }}>
                  Slides
                  <Typography component="span" sx={{ ml: 1, fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>{slides.length}</Typography>
                </Typography>
                {!previewMode && slides.length > 1 && (
                  <>
                    <Button size="small" onClick={collapseAll}>Collapse all</Button>
                    <Button size="small" onClick={expandAll}>Expand all</Button>
                  </>
                )}
                {!previewMode && (
                  <Button onClick={addSlide} startIcon={<Add />} variant="contained" size="small">
                    Add slide
                  </Button>
                )}
              </Box>

              {slides.length === 0 ? (
                <Box sx={{ bgcolor: 'background.paper', border: '1px dashed var(--q-border-hover)', borderRadius: 'var(--q-radius-card)' }}>
                  <EmptyState
                    compact
                    icon={<SlideshowOutlined />}
                    title="No slides yet"
                    description={isEditing ? 'Add a slide to start building this deck.' : 'Import a .pptx above or add a slide to start.'}
                    actions={<Button onClick={addSlide} startIcon={<Add />} variant="contained">Add first slide</Button>}
                  />
                </Box>
              ) : (
                <Box>
                  {slides.map((slide, index) => (
                    <DraggableSlide
                      key={slide.id}
                      index={index}
                      slide={slide}
                      updateSlide={updateSlide}
                      removeSlide={removeSlide}
                      moveSlide={moveSlide}
                      collapsed={!!collapsedById[slide.id]}
                      onToggleCollapsed={toggleSlideCollapsed}
                      readOnly={previewMode}
                    />
                  ))}
                </Box>
              )}

              {errors.slides && <Alert severity="error">{errors.slides}</Alert>}
            </Box>

            <Box component="aside" aria-label="Template data" sx={{ flex: '1 0 300px', maxWidth: { xs: 'none', lg: 380 }, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
              <PowerPointJsonPanel generatedJson={generatedJson} isCopied={isCopied} onCopy={copyToClipboard} />
            </Box>
          </Box>
        </Box>

        <PowerPointHelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
      </Box>
    </DndProvider>
  );
}

export default PowerPointRichTemplateEditor;
